/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Toast Context
const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((toast) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { ...toast, id }]);
    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const success = useCallback((message, options = {}) => {
    return addToast({ type: 'success', message, ...options });
  }, [addToast]);

  const error = useCallback((message, options = {}) => {
    return addToast({ type: 'error', message, ...options });
  }, [addToast]);

  const warning = useCallback((message, options = {}) => {
    return addToast({ type: 'warning', message, ...options });
  }, [addToast]);

  const info = useCallback((message, options = {}) => {
    return addToast({ type: 'info', message, ...options });
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, success, error, warning, info }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// Toast Container
function ToastContainer({ toasts, onRemove }) {
  if (toasts.length === 0) return null;

  return createPortal(
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[300] space-y-2 max-w-sm w-full px-4 pointer-events-none">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onRemove={() => onRemove(toast.id)} />
      ))}
    </div>,
    document.body
  );
}

// Individual Toast
function ToastItem({ toast, onRemove }) {
  const { t } = useTranslation();
  const [isExiting, setIsExiting] = useState(false);

  const handleRemove = useCallback(() => {
    if (isExiting) return;
    setIsExiting(true);
    setTimeout(onRemove, 300);
  }, [isExiting, onRemove]);

  // Auto-dismiss with fade animation
  useEffect(() => {
    if (toast.duration === 0) return;
    const timer = setTimeout(handleRemove, toast.duration || 5000);
    return () => clearTimeout(timer);
  }, [toast.duration, handleRemove]);

  const icons = {
    success: CheckCircle,
    error: XCircle,
    warning: AlertCircle,
    info: Info,
  };

  const styles = {
    success: 'bg-green-50/90 dark:bg-green-900/80 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 backdrop-blur-md',
    error: 'bg-red-50/90 dark:bg-red-900/80 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 backdrop-blur-md',
    warning: 'bg-amber-50/90 dark:bg-amber-900/80 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 backdrop-blur-md',
    info: 'bg-blue-50/90 dark:bg-blue-900/80 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 backdrop-blur-md',
  };

  const iconColors = {
    success: 'text-green-500',
    error: 'text-red-500',
    warning: 'text-amber-500',
    info: 'text-blue-500',
  };

  const Icon = icons[toast.type] || Info;

  return (
    <div
      className={`
        pointer-events-auto flex items-start gap-3 p-4 rounded-lg border shadow-lg
        ${styles[toast.type]}
        ${isExiting ? 'toast-exit' : 'toast-enter'}
      `}
      role="alert"
    >
      <Icon className={`w-5 h-5 flex-shrink-0 ${iconColors[toast.type]} ${toast.type === 'success' ? 'animate-success-pop' : ''}`} />
      <div className="flex-1 min-w-0">
        {toast.title && (
          <p className="font-semibold text-sm mb-0.5">{toast.title}</p>
        )}
        <p className="text-sm">{toast.message}</p>
      </div>
      <button
        onClick={handleRemove}
        className="flex-shrink-0 p-1 rounded hover:bg-black/10 transition-colors"
        aria-label={t('actions.dismiss')}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

// Animated success checkmark
export function SuccessAnimation({ size = 64, className = '' }) {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div
        className="rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center animate-success-pop"
        style={{ width: size, height: size }}
      >
        <svg
          className="text-green-500"
          style={{ width: size * 0.5, height: size * 0.5 }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" className="animate-checkmark" />
        </svg>
      </div>
    </div>
  );
}

// Animated error X
export function ErrorAnimation({ size = 64, className = '' }) {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div
        className="rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center animate-error-shake"
        style={{ width: size, height: size }}
      >
        <XCircle
          className="text-red-500"
          style={{ width: size * 0.5, height: size * 0.5 }}
        />
      </div>
    </div>
  );
}

export default ToastProvider;
